import { create } from 'zustand'
import { deviceApi } from '../lib/deviceApi'

// State for the Data Logger (Live) device-settings page.
// Holds the currently-viewed device, its Sample/Log/Transmit config, and the
// async status of device actions (name update / reset / sync clock).
export const useDeviceStore = create((set, get) => ({
  device: null,
  config: null,
  isLoading: false,
  error: null,
  // Per-action transient status: { action: 'idle'|'pending'|'success'|'error', message }
  action: { name: 'idle', reset: 'idle', syncClock: 'idle', formatMemory: 'idle', intervals: 'idle', firmwareBase: 'idle', firmwareRtu: 'idle', message: null },

  /** Load device header + config together. */
  loadDevice: async (projectId, deviceId) => {
    set({ isLoading: true, error: null })
    try {
      const [detail, config] = await Promise.all([
        deviceApi.fetchDevice(projectId, deviceId),
        deviceApi.fetchConfig(projectId, deviceId),
      ])
      if (!detail.success) throw new Error(detail.error || 'Failed to load device')
      set({
        device: detail.device,
        config: config.success ? config : null,
        isLoading: false,
        error: config.success ? null : (config.error || null),
      })
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to load device'
      set({ isLoading: false, error: message })
      return { success: false, error: message }
    }
  },

  _setAction: (key, status, message = null) =>
    set((s) => ({ action: { ...s.action, [key]: status, message } })),

  /** Update the device name; refreshes local state on success. */
  updateName: async (projectId, deviceId, name) => {
    get()._setAction('name', 'pending')
    try {
      const res = await deviceApi.updateName(projectId, deviceId, name)
      if (!res.success) throw new Error(res.error || 'Update failed')
      set((s) => ({ device: s.device ? { ...s.device, name: res.name } : s.device }))
      get()._setAction('name', 'success', res.message)
      return { success: true, queuedToDevice: res.queued_to_device }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Update failed'
      get()._setAction('name', 'error', message)
      return { success: false, error: message }
    }
  },

  /** Queue a device-level command (reset / syncClock / formatMemory).
   *  `arg` is the command's type where applicable: reset 0=RTU/1=RTU+MAIN,
   *  formatMemory 0=log data/1=sensor config. syncClock ignores it. */
  runAction: async (key, projectId, deviceId, arg) => {
    const fnMap = {
      reset: deviceApi.resetDevice,
      syncClock: deviceApi.syncClock,
      formatMemory: deviceApi.formatMemory,
    }
    const fn = fnMap[key]
    if (!fn) return { success: false, error: `Unknown action: ${key}` }
    get()._setAction(key, 'pending')
    try {
      const res = await fn(projectId, deviceId, arg)
      if (!res.success) throw new Error(res.error || 'Command failed')
      get()._setAction(key, 'success', res.message)
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Command failed'
      get()._setAction(key, 'error', message)
      return { success: false, error: message }
    }
  },

  /** Update a logger to the latest firmware of a type ('base' | 'rtu').
   *  Only QUEUES the command — we deliberately do NOT reload the device after:
   *  the reported version won't change until the logger downloads and installs
   *  the file over FTP (minutes+), and an instant loadDevice() just flashes the
   *  tab into its loading state and wipes the "Queued" confirmation. */
  updateFirmware: async (projectId, deviceId, type) => {
    const key = type === 'rtu' ? 'firmwareRtu' : 'firmwareBase'
    get()._setAction(key, 'pending')
    try {
      const res = await deviceApi.updateFirmware(projectId, deviceId, type)
      if (!res.success) throw new Error(res.error || 'Firmware update failed')
      get()._setAction(key, 'success', res.message || 'Queued')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Firmware update failed'
      get()._setAction(key, 'error', message)
      return { success: false, error: message }
    }
  },

  /** Cancel a QUEUED firmware update of a type ('base' | 'rtu'). Resets the
   *  per-type action status so the card reverts to "Update to Latest". */
  cancelFirmware: async (projectId, deviceId, type) => {
    const key = type === 'rtu' ? 'firmwareRtu' : 'firmwareBase'
    try {
      const res = await deviceApi.cancelFirmware(projectId, deviceId, type)
      if (!res.success) throw new Error(res.error || 'Cancel failed')
      get()._setAction(key, 'idle', null)
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Cancel failed'
      return { success: false, error: message }
    }
  },

  /** Set device-wide Log/Transmit intervals, then reload config to reflect it. */
  setIntervals: async (projectId, deviceId, { logMin, transmitMin } = {}) => {
    get()._setAction('intervals', 'pending')
    try {
      const res = await deviceApi.setIntervals(projectId, deviceId, { logMin, transmitMin })
      if (!res.success) throw new Error(res.error || 'Failed to set intervals')
      get()._setAction('intervals', 'success', res.message || 'Queued')
      await get().loadDevice(projectId, deviceId)  // refresh displayed values
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to set intervals'
      get()._setAction('intervals', 'error', message)
      return { success: false, error: message }
    }
  },

  /** Apply full config (per-sensor intervals + device transmit + transmit flags). */
  applyConfig: async (projectId, deviceId, payload) => {
    get()._setAction('intervals', 'pending')
    try {
      const res = await deviceApi.applyConfig(projectId, deviceId, payload)
      if (!res.success) throw new Error(res.error || 'Failed to apply config')
      get()._setAction('intervals', 'success', res.message || 'Queued')
      await get().loadDevice(projectId, deviceId)
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to apply config'
      get()._setAction('intervals', 'error', message)
      return { success: false, error: message }
    }
  },

  clearActionStatus: (key) => get()._setAction(key, 'idle', null),
  clearError: () => set({ error: null }),
  reset: () => set({ device: null, config: null, isLoading: false, error: null,
    action: { name: 'idle', reset: 'idle', syncClock: 'idle', formatMemory: 'idle', intervals: 'idle', firmwareBase: 'idle', firmwareRtu: 'idle', message: null } }),
}))
